// ============================================================================
// Employee Compliance Center
// Central hub for employees to complete and view their compliance documents
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText,
  Shield,
  Calendar,
  ChevronRight,
  Download,
  Eye,
  ClipboardCheck,
  GraduationCap,
  Scale,
  UserCheck,
  Building2,
  FileCheck,
  Bell,
  Award,
  Lock,
  Upload,
} from 'lucide-react';
import { db } from '@/lib/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAuthStore } from '@/store/authStore';

// Import compliance components
import FormI9 from '../../shared/compliance/FormI9';
import FormW4 from '../../shared/compliance/FormW4';
import FormDE4 from '../../shared/compliance/FormDE4';
import CaliforniaNoticesViewer from '../../shared/compliance/CaliforniaNoticesViewer';
import WageTheftNotice from '../../shared/compliance/WageTheftNotice';
import PolicyAcknowledgment from '../../shared/compliance/PolicyAcknowledgment';
import { COMPANY_POLICIES } from '@/lib/policyTemplates';
import { syncProfileFromForm, getProfileDataForForms } from '@/lib/profileSync';

// Alias for compatibility
const POLICY_TEMPLATES = COMPANY_POLICIES;
import { CA_MANDATORY_NOTICES as CA_NOTICES } from '@/lib/californiaForms';
import { getStateTaxRequirements } from '@/lib/complianceData';
import GenericStateTaxForm from '../../shared/compliance/GenericStateTaxForm';

// ============================================================================
// COMPLIANCE TASK CARD
// ============================================================================

function ComplianceTaskCard({
  title,
  description,
  status,
  icon: Icon,
  iconColor = 'blue',
  deadline,
  onStart,
  onView,
  onDownload,
  onUpload,
  uploadPending,
  required = true,
  locked = false,
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    gray: 'bg-gray-100 text-gray-600',
  };

  const getStatusBadge = () => {
    switch (status) {
      case 'complete':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Complete
          </Badge>
        );
      case 'pending_acknowledgment':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200 animate-pulse">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Action Required
          </Badge>
        );
      case 'update_requested':
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 animate-pulse">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Update Requested
          </Badge>
        );
      case 'in_progress':
      case 'draft':
      case 'section2_in_progress':
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200">
            <Clock className="h-3 w-3 mr-1" />
            In Progress
          </Badge>
        );
      case 'section1_complete':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <Clock className="h-3 w-3 mr-1" />
            Awaiting HR Review
          </Badge>
        );
      case 'pending_review':
      case 'pending_employer_review':
        return (
          <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-600">
            {required ? 'Required' : 'Optional'}
          </Badge>
        );
    }
  };

  return (
    <Card className={`transition-all hover:shadow-md ${locked ? 'opacity-60' : ''} ${status === 'complete' ? 'border-green-200' : ''
      }`}>
      <CardContent className="pt-5">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${locked ? 'bg-gray-100' : colorClasses[iconColor]
            }`}>
            {locked ? <Lock className="h-6 w-6 text-gray-400" /> : <Icon className="h-6 w-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-gray-900">{title}</h3>
              {getStatusBadge()}
            </div>
            <p className="text-sm text-gray-500 mb-3">{description}</p>

            {deadline && status !== 'complete' && (
              <p className="text-xs text-orange-600 flex items-center gap-1 mb-3">
                <Calendar className="h-3 w-3" />
                Due: {new Date(deadline).toLocaleDateString()}
              </p>
            )}

            {/* Show message for I-9 Section 1 complete */}
            {status === 'section1_complete' && (
              <Alert className="mb-3 bg-amber-50 border-amber-200">
                <Clock className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-xs">
                  Section 1 complete! Please upload your identity documents to pending review.
                </AlertDescription>
              </Alert>
            )}

            {/* Show message for section 2 in progress (uploaded) */}
            {status === 'section2_in_progress' && (
              <Alert className="mb-3 bg-blue-50 border-blue-200">
                <CheckCircle2 className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-xs">
                  Documents uploaded. Awaiting final HR verification.
                </AlertDescription>
              </Alert>
            )}

            {/* Show message for WTPN pending acknowledgment */}
            {status === 'pending_acknowledgment' && (
              <Alert className="mb-3 bg-red-50 border-red-200">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 text-xs">
                  HR has issued a new notice for you. Please review and acknowledge it immediately.
                </AlertDescription>
              </Alert>
            )}

            {/* Show message for form update requested */}
            {status === 'update_requested' && (
              <Alert className="mb-3 bg-orange-50 border-orange-200">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800 text-xs">
                  HR has requested you update this form. Please review and submit your updated information.
                </AlertDescription>
              </Alert>
            )}

            {/* Show message for pending employer review */}
            {status === 'pending_employer_review' && (
              <Alert className="mb-3 bg-blue-50 border-blue-200">
                <Clock className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-xs">
                  Submitted. Your employer will review and finalize this form.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center gap-2">
              {status === 'complete' ? (
                <>
                  <Button variant="outline" size="sm" onClick={onView}>
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  {onDownload && (
                    <Button variant="ghost" size="sm" onClick={onDownload}>
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  )}
                </>
              ) : status === 'pending_acknowledgment' ? (
                <Button size="sm" onClick={onStart} className="bg-red-600 hover:bg-red-700">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Review & Acknowledge
                </Button>
              ) : status === 'update_requested' ? (
                <Button size="sm" onClick={onStart} className="bg-orange-600 hover:bg-orange-700">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Update Now
                </Button>
              ) : status === 'section1_complete' || status === 'section2_in_progress' ? (
                <>
                  <Button variant="outline" size="sm" onClick={onView}>
                    <Eye className="h-4 w-4 mr-1" />
                    View Status
                  </Button>
                  {onUpload && (
                    <Button size="sm" onClick={onUpload} className="bg-amber-600 hover:bg-amber-700">
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadPending ? 'Continue Upload' : 'Upload Documents'}
                    </Button>
                  )}
                </>
              ) : locked ? (
                <p className="text-sm text-gray-400">Complete previous steps first</p>
              ) : status === 'pending_employer_review' ? (
                <Button variant="outline" size="sm" onClick={onView}>
                  <Eye className="h-4 w-4 mr-1" />
                  View Status
                </Button>
              ) : (
                <Button size="sm" onClick={onStart}>
                  {status === 'draft' || status === 'in_progress' ? 'Continue' : 'Start'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card >
  );
}

// ============================================================================
// PROGRESS HEADER
// ============================================================================

function ProgressHeader({ completedCount, totalCount, employeeName }) {
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <Card className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-0">
      <CardContent className="py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1">Welcome, {employeeName}!</h1>
            <p className="text-blue-100">
              {percentage === 100
                ? 'Congratulations! All compliance tasks are complete.'
                : 'Complete your compliance tasks below to finalize your onboarding.'
              }
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{percentage}%</div>
            <p className="text-blue-100 text-sm">Compliance Complete</p>
          </div>
        </div>
        <div className="mt-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-blue-100">{completedCount} of {totalCount} tasks complete</span>
            {percentage < 100 && (
              <span className="text-blue-100">{totalCount - completedCount} remaining</span>
            )}
          </div>
          <Progress value={percentage} className="h-3 bg-blue-500/30" />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN COMPLIANCE CENTER COMPONENT
// ============================================================================

export default function EmployeeComplianceCenter() {
  const { user } = useAuthStore();
  const [activeDialog, setActiveDialog] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Use current user directly (user from authStore is the employee)
  const employee = user;

  // Get form submissions
  const formSubmissions = useLiveQuery(
    () => db.formSubmissions?.where('userId').equals(user?.id || '').toArray(),
    [user?.id]
  ) || [];

  // Get I-9 submissions
  const i9Submissions = useLiveQuery(
    () => db.i9Submissions?.where('userId').equals(user?.id || '').toArray(),
    [user?.id]
  ) || [];

  // Get policy acknowledgments
  const policyAcks = useLiveQuery(
    () => db.policyAcknowledgments?.where('userId').equals(user?.id || '').toArray(),
    [user?.id]
  ) || [];

  // Get training assignments
  const trainingAssignments = useLiveQuery(
    () => db.trainingAssignments?.where('userId').equals(user?.id || '').toArray(),
    [user?.id]
  ) || [];

  // Get signatures
  const signatures = useLiveQuery(
    () => db.eSignatures?.where('userId').equals(user?.id || '').toArray(),
    [user?.id]
  ) || [];

  // Calculate compliance status
  const complianceStatus = useMemo(() => {
    // Helper to find most relevant submission (prioritize incomplete, then most recent)
    const findRelevantSubmission = (type) => {
      const submissions = formSubmissions
        .filter(s => s.formType === type)
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

      // Return the most recent incomplete one, or the most recent complete one if all are complete
      // Correction: Treat 'pending_employer_review' as a definitive status we want to show, effectively "active"
      return submissions.find(s => s.status !== 'complete' && s.status !== 'pending_employer_review')
        || submissions[0]
        || { status: 'not_started' };
    };

    const i9 = i9Submissions[0] || { status: 'not_started' };
    const w4 = findRelevantSubmission('W-4');

    // Dynamic State Tax Logic
    const employeeState = employee?.profile?.address?.state || employee?.address?.state || 'CA';
    const stateTaxReqs = getStateTaxRequirements(employeeState);

    // Find submission matching the state's requirement
    // If it is CA, look for DE-4. If generic, look for STATE_TAX_[STATE]
    const stateFormType = stateTaxReqs.formCode || `STATE_TAX_${employeeState}`;
    const stateTax = findRelevantSubmission(stateFormType);

    const wtpn = findRelevantSubmission('WTPN');
    const caNotices = findRelevantSubmission('CA_NOTICES');

    // Policy acknowledgments
    const requiredPolicies = Object.values(POLICY_TEMPLATES).filter(p => p.required);
    const acknowledgedPolicies = policyAcks.filter(a => a.status === 'acknowledged');
    const policiesComplete = acknowledgedPolicies.length >= requiredPolicies.length;

    // Training
    const requiredTraining = trainingAssignments.filter(t => t.required);
    const completedTraining = trainingAssignments.filter(t => t.status === 'completed');
    const trainingComplete = completedTraining.length >= requiredTraining.length || requiredTraining.length === 0;

    // Count completed items
    const tasks = [
      { key: 'i9', status: i9.status },
      { key: 'w4', status: w4.status },
      // Only count state tax if required
      ...(stateTaxReqs.hasIncomeTax && !stateTaxReqs.usesFederalW4 ? [{ key: 'stateTax', status: stateTax.status }] : []),
      { key: 'wtpn', status: wtpn.status },
      { key: 'caNotices', status: caNotices.status },
      { key: 'policies', status: policiesComplete ? 'complete' : 'not_started' },
      { key: 'training', status: trainingComplete ? 'complete' : 'not_started' },
    ];

    const completedCount = tasks.filter(t => t.status === 'complete' || t.status === 'pending_employer_review').length;

    return {
      i9,
      w4,
      stateTax,
      stateTaxReqs, // Pass validation rules
      wtpn,
      caNotices,
      policiesComplete,
      trainingComplete,
      acknowledgedPolicies,
      completedTraining,
      completedCount,
      totalCount: tasks.length,
    };
  }, [formSubmissions, i9Submissions, policyAcks, trainingAssignments, employee]);

  // Open dialog for form
  const openFormDialog = (formType, submission = null) => {
    setSelectedItem(submission);
    setActiveDialog(formType);
  };

  // Close dialog
  const closeDialog = () => {
    setActiveDialog(null);
    setSelectedItem(null);
  };

  // Save form submission
  const saveFormSubmission = async (formType, data) => {
    try {
      const submission = {
        id: selectedItem?.id || `${formType}_${employee.id}_${Date.now()}`,
        formType,
        userId: employee.id,
        data,
        status: data.status || 'draft',
        createdAt: selectedItem?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (formType === 'I-9') {
        await db.i9Submissions.put(submission);
      } else {
        await db.formSubmissions.put(submission);
      }

      // Sync form data back to user profile (only for completed forms or I-9 Section 1)
      if (data.status === 'complete' || (formType === 'I-9' && data.status === 'section1_complete')) {
        await syncProfileFromForm(employee.id, formType, data);
      }

      // Log to audit
      await db.complianceAuditLog.add({
        id: `log_${Date.now()}`,
        action: 'form_submitted',
        timestamp: new Date().toISOString(),
        userId: employee.id,
        employeeName: `${employee.profile?.firstName || ''} ${employee.profile?.lastName || ''}`,
        documentType: formType,
        description: `${formType} form ${data.status === 'complete' ? 'completed' : data.status === 'section1_complete' ? 'Section 1 completed - awaiting employer review' : 'saved as draft'}`,
      });

    } catch (error) {
      console.error('Error saving form:', error);
    }
  };

  // Submit form (complete)
  const submitForm = async (formType, data) => {
    // For I-9, preserve the status if it's section1_complete (don't override to complete)
    const finalStatus = formType === 'I-9' && data.status === 'section1_complete'
      ? 'section1_complete'
      : data.status || 'complete';

    await saveFormSubmission(formType, {
      ...data,
      status: finalStatus,
      submittedAt: new Date().toISOString()
    });
    closeDialog();
  };

  // Save I-9 Section 2 (employee uploading documents)
  const handleSaveI9Section2 = async (section2Data) => {
    try {
      if (!i9Submissions || i9Submissions.length === 0) {
        console.error('No existing I-9 submission found');
        return;
      }
      const existingI9 = i9Submissions[0];

      const updatedSubmission = {
        ...existingI9,
        section2: section2Data,
        status: 'section2_in_progress',
        data: {
          ...existingI9.data,
          section2: section2Data,
        },
        updatedAt: new Date().toISOString(),
      };

      await db.i9Submissions.put(updatedSubmission);

      console.log('I-9 Section 2 documents saved');
      toast.success('Documents Uploaded', {
        description: 'Your documents have been uploaded successfully. HR will review them shortly.',
      });
      closeDialog();
    } catch (error) {
      console.error('Error saving I-9 Section 2:', error);
      toast.error('Upload Failed', {
        description: 'Failed to save documents. Please try again.',
      });
    }
  };

  // Acknowledge policy
  const acknowledgePolicy = async (policyId, data) => {
    try {
      const ack = {
        id: `ack_${policyId}_${employee.id}`,
        policyId,
        policyCode: policyId, // Store as policyCode for admin dashboard compatibility
        userId: employee.id,
        status: 'acknowledged',
        acknowledgedAt: new Date().toISOString(),
        signature: data.signature,
      };

      await db.policyAcknowledgments.put(ack);

      await db.complianceAuditLog.add({
        id: `log_${Date.now()}`,
        action: 'policy_acknowledged',
        timestamp: new Date().toISOString(),
        userId: employee.id,
        employeeName: `${employee.profile?.firstName || ''} ${employee.profile?.lastName || ''}`,
        documentType: policyId,
        description: `Policy acknowledged: ${policyId}`,
      });

      closeDialog();
    } catch (error) {
      console.error('Error acknowledging policy:', error);
    }
  };

  // Get employer data for WTPN
  const employer = useMemo(() => ({
    name: 'SpaceHR Technologies Inc.',
    dba: '',
    address: '123 Innovation Drive',
    city: 'San Francisco',
    state: 'CA',
    zip: '94105',
    phone: '(415) 555-0100',
    fein: 'XX-XXXXXXX',
    workersCompCarrier: 'State Fund Insurance',
    workersCompPhone: '(800) 555-0199',
    workersCompPolicy: 'WC-123456789',
    payDay: 'Friday',
  }), []);

  if (!employee) {
    return (
      <div className="p-6 text-center">
        <p>Loading employee data...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Progress Header */}
      <ProgressHeader
        completedCount={complianceStatus.completedCount}
        totalCount={complianceStatus.totalCount}
        employeeName={employee?.profile?.firstName || employee?.name?.split(' ')[0] || 'Team Member'}
      />

      {/* Urgent Alert */}
      {complianceStatus.completedCount < complianceStatus.totalCount && (
        <Alert className="bg-amber-50 border-amber-200">
          <Bell className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Action Required</AlertTitle>
          <AlertDescription className="text-amber-700">
            Please complete all compliance tasks within your first 3 days of employment.
            Forms must be completed for payroll processing.
          </AlertDescription>
        </Alert>
      )}

      {/* Compliance Sections */}
      <Tabs defaultValue="forms" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="forms" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Tax Forms
          </TabsTrigger>
          <TabsTrigger value="notices" className="flex items-center gap-2">
            <Scale className="h-4 w-4" />
            Notices
          </TabsTrigger>
          <TabsTrigger value="policies" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="training" className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            Training
          </TabsTrigger>
        </TabsList>

        {/* Tax Forms Tab */}
        <TabsContent value="forms" className="space-y-4">
          <div className="grid gap-4">
            <ComplianceTaskCard
              title="Form I-9: Employment Eligibility Verification"
              description="Verify your identity and employment authorization. Required for all US employees."
              status={complianceStatus.i9.status}
              icon={UserCheck}
              iconColor="blue"
              required
              onStart={() => openFormDialog('I-9', complianceStatus.i9)}
              onView={() => openFormDialog('I-9', complianceStatus.i9)}
              onUpload={() => openFormDialog('I-9', complianceStatus.i9)} // Opens same dialog, logic inside handles section 2 view
              uploadPending={complianceStatus.i9.status === 'section1_complete'}
            />

            <ComplianceTaskCard
              title="Form W-4: Federal Tax Withholding"
              description="Set your federal income tax withholding. Determines how much tax is withheld from your paycheck."
              status={complianceStatus.w4.status}
              icon={FileCheck}
              iconColor="green"
              required
              onStart={() => openFormDialog('W-4', complianceStatus.w4)}
              onView={() => openFormDialog('W-4', complianceStatus.w4)}
            />

            {/* State Tax Card (Dynamic) */}
            {complianceStatus.stateTaxReqs.hasIncomeTax ? (
              complianceStatus.stateTaxReqs.usesFederalW4 ? (
                <Card className="bg-gray-50 border-dashed border-gray-300">
                  <CardContent className="pt-6 flex items-center gap-4 text-gray-500">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <FileCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">State Tax Withholding</h3>
                      <p className="text-sm">Your state ({employee?.profile?.address?.state || 'Unknown'}) uses the Federal W-4. No separate state form required.</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <ComplianceTaskCard
                  title={`Form ${complianceStatus.stateTaxReqs.formCode || 'State Tax'}: ${complianceStatus.stateTaxReqs.formName}`}
                  description={`Set your ${employee?.profile?.address?.state || 'State'} income tax withholding. Required for your location.`}
                  status={complianceStatus.stateTax.status}
                  icon={FileCheck}
                  iconColor="amber"
                  required
                  onStart={() => openFormDialog('STATE_TAX', complianceStatus.stateTax)}
                  onView={() => openFormDialog('STATE_TAX', complianceStatus.stateTax)}
                />
              )
            ) : (
              <Card className="bg-gray-50 border-dashed border-gray-300">
                <CardContent className="pt-6 flex items-center gap-4 text-gray-500">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <FileCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">State Tax Withholding</h3>
                    <p className="text-sm">No state income tax for {employee?.profile?.address?.state}, or no form required.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Notices Tab */}
        <TabsContent value="notices" className="space-y-4">
          <div className="grid gap-4">
            <ComplianceTaskCard
              title="Wage Theft Prevention Notice"
              description="California Labor Code §2810.5 notice about your pay rate, schedule, and employer information."
              status={complianceStatus.wtpn.status}
              icon={Scale}
              iconColor="red"
              required
              onStart={() => openFormDialog('WTPN', complianceStatus.wtpn)}
              onView={() => openFormDialog('WTPN', complianceStatus.wtpn)}
            />

            <ComplianceTaskCard
              title="California Employment Notices"
              description="Review and acknowledge required California workplace notices including sick leave, minimum wage, and workers' comp."
              status={complianceStatus.caNotices.status}
              icon={Building2}
              iconColor="purple"
              required
              onStart={() => openFormDialog('CA_NOTICES', complianceStatus.caNotices)}
              onView={() => openFormDialog('CA_NOTICES', complianceStatus.caNotices)}
            />
          </div>
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-purple-600" />
                Company Policies
              </CardTitle>
              <CardDescription>
                Review and acknowledge the following company policies
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.values(POLICY_TEMPLATES).map((policy) => {
                const isAcknowledged = policyAcks.some(a =>
                  a.policyCode === policy.policyCode || a.policyId === policy.policyCode
                ); // Use policyCode

                return (
                  <div
                    key={policy.policyCode}
                    className={`flex items-center justify-between p-4 rounded-lg border ${isAcknowledged ? 'bg-green-50 border-green-200' : 'bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      {isAcknowledged ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <FileText className="h-5 w-5 text-gray-400" />
                      )}
                      <div>
                        <p className="font-medium">{policy.title}</p>
                        <p className="text-sm text-gray-500">{policy.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {policy.required && !isAcknowledged && (
                        <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                          Required
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant={isAcknowledged ? 'outline' : 'default'}
                        onClick={() => openFormDialog('POLICY', { ...policy, acknowledged: isAcknowledged })}
                      >
                        {isAcknowledged ? 'View' : 'Review & Sign'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-blue-600" />
                Required Training
              </CardTitle>
              <CardDescription>
                Complete the following training modules
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trainingAssignments.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900">No Training Assigned Yet</h3>
                  <p className="text-gray-500 mt-2">
                    Training modules will appear here once assigned by your manager.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {trainingAssignments.map((training) => {
                    const isComplete = training.status === 'completed';
                    const isOverdue = !isComplete && new Date(training.dueDate) < new Date();

                    return (
                      <div
                        key={training.id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${isComplete ? 'bg-green-50 border-green-200' :
                          isOverdue ? 'bg-red-50 border-red-200' : 'bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          {isComplete ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : isOverdue ? (
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                          ) : (
                            <Clock className="h-5 w-5 text-blue-600" />
                          )}
                          <div>
                            <p className="font-medium">{training.title}</p>
                            <p className="text-sm text-gray-500">
                              {isComplete
                                ? `Completed on ${new Date(training.completedAt).toLocaleDateString()}`
                                : `Due: ${new Date(training.dueDate).toLocaleDateString()}`
                              }
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={isComplete ? 'outline' : isOverdue ? 'destructive' : 'default'}
                        >
                          {isComplete ? 'View Certificate' : 'Start Training'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Form Dialogs */}
      <Dialog open={activeDialog === 'I-9'} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <FormI9
            employee={employee}
            existingSubmission={selectedItem?.status !== 'not_started' ? (selectedItem?.data || selectedItem) : null}
            onSaveSection1={(data) => saveFormSubmission('I-9', { ...data, status: 'draft' })}
            onSubmitSection1={(data) => submitForm('I-9', { ...data, status: 'section1_complete', section1Complete: true })}
            onSaveSection2={handleSaveI9Section2}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'W-4'} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <FormW4
            employee={employee}
            existingSubmission={selectedItem?.status !== 'not_started' ? { ...(selectedItem?.data || selectedItem), status: selectedItem?.status } : null}
            onSave={(data) => saveFormSubmission('W-4', data)}
            onSubmit={(data) => submitForm('W-4', data)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'STATE_TAX'} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {complianceStatus.stateTaxReqs?.component === 'FormDE4' ? (
            <FormDE4
              employee={employee}
              existingSubmission={selectedItem?.status !== 'not_started' ? { ...(selectedItem?.data || selectedItem), status: selectedItem?.status } : null}
              onSave={(data) => saveFormSubmission('DE-4', data)}
              onSubmit={(data) => submitForm('DE-4', data)}
            />
          ) : (
            <GenericStateTaxForm
              stateCode={employee?.profile?.address?.state || ''}
              stateName={complianceStatus.stateTaxReqs?.formName || 'State Tax'}
              downloadUrl={complianceStatus.stateTaxReqs?.downloadUrl}
              employee={employee}
              existingSubmission={selectedItem?.status !== 'not_started' ? selectedItem : null}
              onSubmit={(data) => submitForm(complianceStatus.stateTaxReqs?.formCode || `STATE_TAX_${employee?.profile?.address?.state || 'GENERIC'}`, data)}
              onCancel={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'WTPN'} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <WageTheftNotice
            employee={employee}
            employer={employer}
            existingSubmission={selectedItem?.status !== 'not_started' ? { ...(selectedItem?.data || selectedItem), status: selectedItem?.status } : null}
            onSave={(data) => saveFormSubmission('WTPN', data)}
            onSubmit={(data) => submitForm('WTPN', data)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'CA_NOTICES'} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <CaliforniaNoticesViewer
            employee={employee}
            existingSubmission={selectedItem?.status !== 'not_started' ? { ...(selectedItem?.data || selectedItem), status: selectedItem?.status } : null}
            onComplete={(data) => submitForm('CA_NOTICES', data)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'POLICY'} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <PolicyAcknowledgment
              policy={selectedItem}
              employee={employee}
              existingAcknowledgment={selectedItem.acknowledged ? { status: 'acknowledged' } : null}
              onAcknowledge={(data) => acknowledgePolicy(selectedItem.policyCode, data)}
              onCancel={() => closeDialog()}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
